export interface ScrollToWhenReadyOptions {
    /**
     * Milliseconds to wait after the element first appears before scrolling. Use this when the
     * element mounts behind an entrance animation (e.g. an AnimatePresence transition) so the
     * layout has settled and we measure its final position. Defaults to 0 (scroll immediately).
     */
    settleMs?: number;
    /** Vertical alignment passed to scrollIntoView. Defaults to 'start'. */
    block?: ScrollLogicalPosition;
    /**
     * Element id to move keyboard focus to once revealed. Defaults to the scrolled element.
     * Focus uses preventScroll so it does not fight the smooth scroll.
     */
    focusId?: string;
    /** Max number of animation frames to wait for the element to mount. Defaults to 60. */
    maxAttempts?: number;
    /** Called once the element is found and revealed. */
    onReveal?: (element: HTMLElement) => void;
}

/**
 * Polls for an element by id (across animation frames), then scrolls it into view and moves
 * focus to it. Handles two cases the naive `getElementById + scrollIntoView` misses: the element
 * not being mounted yet, and the element mounting behind an entrance animation whose layout has
 * not settled (pass `settleMs`).
 */
export function scrollToWhenReady(id: string, options?: ScrollToWhenReadyOptions): void {
    const { settleMs = 0, block = 'start', focusId, maxAttempts = 60, onReveal } = options ?? {};

    const reveal = (element: HTMLElement) => {
        element.scrollIntoView({ behavior: 'smooth', block });
        const focusTarget = focusId ? document.getElementById(focusId) : element;
        focusTarget?.focus({ preventScroll: true });
        onReveal?.(element);
    };

    let attempts = 0;
    const tick = () => {
        const element = document.getElementById(id);
        if (element) {
            if (settleMs > 0) {
                setTimeout(() => reveal(element), settleMs);
            } else {
                reveal(element);
            }
        } else if (attempts < maxAttempts) {
            attempts++;
            requestAnimationFrame(tick);
        }
    };
    requestAnimationFrame(tick);
}
